// Recorte y escalado de la firma antes de subirla. Las funciones de cálculo son
// puras (sin canvas) para poder testearlas en jsdom; `exportarFirma` es el único
// punto que toca el canvas.

// Alpha mínimo para considerar un píxel como tinta (descarta residuos de anti-alias).
const UMBRAL_ALPHA = 8;
// Ancho máximo del PNG exportado. En el PDF la firma se dibuja en ~5.5 cm, así que
// 1200 px sobra para imprimir y mantiene el archivo liviano.
export const MAX_ANCHO_SALIDA = 1200;
// Margen alrededor de la tinta, como fracción del lado mayor de la caja.
const MARGEN_RELATIVO = 0.06;
const MARGEN_MINIMO = 4;

/**
 * Caja mínima que contiene toda la tinta de un buffer RGBA.
 * @param {Uint8ClampedArray} data buffer RGBA (w*h*4).
 * @returns {{x:number,y:number,w:number,h:number}|null} null si no hay tinta.
 */
export function calcularCajaTinta(data, width, height, umbral = UMBRAL_ALPHA) {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    const fila = y * width;
    for (let x = 0; x < width; x += 1) {
      if (data[(fila + x) * 4 + 3] >= umbral) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * Región a recortar: la caja de tinta más un margen, acotada al lienzo.
 * @returns {{sx:number,sy:number,sw:number,sh:number}}
 */
export function calcularRecorte(caja, { ancho, alto }) {
  const margen = Math.max(MARGEN_MINIMO, Math.round(Math.max(caja.w, caja.h) * MARGEN_RELATIVO));
  const sx = Math.max(0, caja.x - margen);
  const sy = Math.max(0, caja.y - margen);
  const x2 = Math.min(ancho, caja.x + caja.w + margen);
  const y2 = Math.min(alto, caja.y + caja.h + margen);
  return { sx, sy, sw: x2 - sx, sh: y2 - sy };
}

/** Tamaño final: reduce (nunca agranda) para no superar `maxAncho`, conservando proporción. */
export function calcularTamanoSalida(sw, sh, maxAncho = MAX_ANCHO_SALIDA) {
  if (sw <= maxAncho) return { w: sw, h: sh };
  const escala = maxAncho / sw;
  return { w: maxAncho, h: Math.max(1, Math.round(sh * escala)) };
}

/**
 * Recorta el canvas a la tinta y devuelve un PNG transparente.
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<Blob>}
 */
export function exportarFirma(canvas) {
  return new Promise((resolve, reject) => {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const caja = calcularCajaTinta(ctx.getImageData(0, 0, width, height).data, width, height);
    if (!caja) {
      reject(new Error('La firma está vacía'));
      return;
    }
    const { sx, sy, sw, sh } = calcularRecorte(caja, { ancho: width, alto: height });
    const { w, h } = calcularTamanoSalida(sw, sh);
    const salida = document.createElement('canvas');
    salida.width = w;
    salida.height = h;
    salida.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, w, h);
    salida.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('No se pudo codificar la firma'));
    }, 'image/png');
  });
}
